import html2canvas from 'html2canvas'
import moment from 'moment'
import React, { FC, useRef, useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { EuiSpacer } from '@elastic/eui'

import { MasterCampaignPlan } from 'api/interfaces/MasterCampaignPlan'
import { MasterCampaignPlanCampaignBuilder } from 'api/interfaces/MasterCampaignStub'
import {
  useGetMasterCampaignPlanMasterCampaignStubQuery,
  useGetMasterCampaignPlanExecutiveSummarySectionQuery,
  useUpdateExecutiveSummaryIntroductionSectionMutation,
  useUpdateChannelUtilizationSectionMutation,
  useUpdateAudienceStrategySectionMutation,
  useUpdateGeographicTargetingSectionMutation,
  useUpdateBudgetSectionMutation,
  useHideExecutiveSummarySectionMutation,
  useAddCustomSectionMutation,
  useUpdateCustomSectionMutation,
  useDeleteCustomSectionMutation,
  useUpdatePageTitleMutation
} from 'api/rtkQueryApi/platform/masterCampaignPlansApi'
import { RootState } from 'app/rootReducer'
import { AcConfirmModal } from 'components/Modals/AcConfirmModal'
import { EditType } from 'features/planner/components/EditFlyout'
import { AddCustomSectionButton } from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/AddCustomSectionButton'
import { downloadPdf } from 'utils/pdfDownloader'

import { selectEditMode } from '../../masterCampaignPlanSlice'
import { selectGanttChartImageData, setGanttChartImage } from '../../pdfViewSlice'
import { getBudgetContent, getChannelUtilizationContent, getPlanDescription } from '../../utils/planContentUtils'
import Budget from '../Budget'
import { RenderCustomSections } from '../shared/RenderCustomSections'
import AudienceAndGeoTargeting from './AudienceAndGeoTargeting'
import CampaignTimeline from './CampaignTimeline'
import ChannelUtilization from './ChannelUtilization'
import CustomizableSection from './CustomizableSection'
import EditCustomSectionFlyout from './EditCustomSectionFlyout'
import EditPageTitleFlyout from './EditPageTitleFlyout'
import ExecutiveSummaryHeader from './ExecutiveSummaryHeader'
import ExecutiveSummaryPdf from './ExecutiveSummaryPdf'
import PlanDescription from './PlanDescription'

type SectionType = 'introduction' | 'channelUtilization' | 'audienceStrategy' | 'geographicTargeting' | 'budget'
export type ExecutiveSummaryCustomSectionPosition = 'aboveCampaignTimeline' | 'aboveChannelUtilization' | 'aboveAudienceAndGeoSummary' | 'aboveBudget' | 'aboveBottom'

export interface ExecutiveSummaryProps {
  masterCampaignPlan: MasterCampaignPlan
  onEditSectionClick?: (editType: EditType) => void
  ganttChartRef?: React.RefObject<HTMLDivElement>
  onAllClientPdfsClick?: () => void
  onAllStaffPdfsClick?: () => void
  staffPdfDisabledReason?: string
}

const generateCampaignsHash = (campaigns: MasterCampaignPlanCampaignBuilder[]): string => {
  const campaignData = campaigns.map(c => ({
    id: c.id,
    name: c.name,
    startDate: c.startDate,
    intervalDuration: c.intervalDuration,
    intervalBudget: c.intervalBudget,
    campaignType: c.campaignType
  }))
  return JSON.stringify(campaignData)
}

const ExecutiveSummary: FC<ExecutiveSummaryProps> = ({ masterCampaignPlan, onEditSectionClick, ganttChartRef: externalGanttChartRef, onAllClientPdfsClick, onAllStaffPdfsClick, staffPdfDisabledReason }) => {
  const dispatch = useDispatch()
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const editMode = useSelector(selectEditMode)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)

  const [editingSectionType, setEditingSectionType] = useState<SectionType | null>(null)
  const [deletingSectionType, setDeletingSectionType] = useState<SectionType | null>(null)
  const [addingCustomSectionPosition, setAddingCustomSectionPosition] = useState<ExecutiveSummaryCustomSectionPosition | null>(null)
  const [editingCustomSection, setEditingCustomSection] = useState<{ position: ExecutiveSummaryCustomSectionPosition; index: number } | null>(null)
  const [deletingCustomSection, setDeletingCustomSection] = useState<{ position: ExecutiveSummaryCustomSectionPosition; index: number } | null>(null)
  const [showBudgetSaveWarning, setShowBudgetSaveWarning] = useState(false)
  const [pendingBudgetSave, setPendingBudgetSave] = useState<{ title: string; content: string; showTitle: boolean } | null>(null)
  const [editingPageTitle, setEditingPageTitle] = useState(false)

  const ganttImageData = useSelector((state: RootState) => selectGanttChartImageData(state, masterCampaignPlan.id))

  const internalGanttChartRef = useRef<HTMLDivElement>(null)
  const ganttChartRef = externalGanttChartRef || internalGanttChartRef

  const { data: masterCampaignStub, isFetching: isMasterCampaignStubFetching } = useGetMasterCampaignPlanMasterCampaignStubQuery({ masterCampaignPlanId: masterCampaignPlan.id })
  const { data: executiveSummarySection } = useGetMasterCampaignPlanExecutiveSummarySectionQuery({ masterCampaignPlanId: masterCampaignPlan.id })

  const [updateIntroduction, { isLoading: isUpdatingIntroduction }] = useUpdateExecutiveSummaryIntroductionSectionMutation()
  const [updateChannelUtilization, { isLoading: isUpdatingChannelUtilization }] = useUpdateChannelUtilizationSectionMutation()
  const [updateAudienceStrategy, { isLoading: isUpdatingAudienceStrategy }] = useUpdateAudienceStrategySectionMutation()
  const [updateGeographicTargeting, { isLoading: isUpdatingGeographicTargeting }] = useUpdateGeographicTargetingSectionMutation()
  const [updateBudget, { isLoading: isUpdatingBudget }] = useUpdateBudgetSectionMutation()
  const [hideSection, { isLoading: isHidingSection }] = useHideExecutiveSummarySectionMutation()
  const [addCustomSection, { isLoading: isAddingCustomSection }] = useAddCustomSectionMutation()
  const [updateCustomSection, { isLoading: isUpdatingCustomSection }] = useUpdateCustomSectionMutation()
  const [deleteCustomSection, { isLoading: isDeletingCustomSection }] = useDeleteCustomSectionMutation()
  const [updatePageTitle, { isLoading: isUpdatingPageTitle }] = useUpdatePageTitleMutation()

  const campaigns = masterCampaignStub?.campaignBuilders || []
  const planStartDate = moment(masterCampaignPlan.aiUsedStartDate)
  const planDurationMonths = masterCampaignPlan.aiUsedDurationInMonths
  const campaignsHash = generateCampaignsHash(campaigns)

  const captureGanttChart = async () => {
    if (ganttChartRef.current) {
      try {
        const canvas = await html2canvas(ganttChartRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true
        })
        const imageData = canvas.toDataURL('image/png')

        dispatch(
          setGanttChartImage({
            planId: masterCampaignPlan.id,
            image: imageData,
            campaignsHash
          })
        )

        return imageData
      } catch (error) {
        console.error('Failed to capture gantt chart:', error)
        return undefined
      }
    }
    return undefined
  }

  const handlePdfDownload = async () => {
    try {
      setIsGeneratingPdf(true)

      const ganttImage = ganttImageData?.image ?? (await captureGanttChart())

      const filename = `${masterCampaignPlan.name} - Executive Summary.pdf`

      await downloadPdf(<ExecutiveSummaryPdf masterCampaignPlan={masterCampaignPlan} campaigns={campaigns} accountName={currentAccount?.name || ''} ganttChartImage={ganttImage} executiveSummarySection={executiveSummarySection} />, filename)
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  const handleEditSection = (sectionType: SectionType) => {
    setEditingSectionType(sectionType)
  }

  const handleDeleteSection = (sectionType: SectionType) => {
    setDeletingSectionType(sectionType)
  }

  const handleSaveSection = async (title: string, content: string, showTitle: boolean) => {
    if (!editingSectionType) return

    if (editingSectionType === 'budget') {
      setPendingBudgetSave({ title, content, showTitle })
      setShowBudgetSaveWarning(true)
      return
    }

    const params = {
      masterCampaignPlanId: masterCampaignPlan.id,
      title,
      content,
      showTitle
    }

    switch (editingSectionType) {
      case 'introduction':
        await updateIntroduction(params)
        break
      case 'channelUtilization':
        await updateChannelUtilization(params)
        break
      case 'audienceStrategy':
        await updateAudienceStrategy(params)
        break
      case 'geographicTargeting':
        await updateGeographicTargeting(params)
        break
    }

    setEditingSectionType(null)
  }

  const confirmBudgetSave = async () => {
    if (!pendingBudgetSave) return

    const params = {
      masterCampaignPlanId: masterCampaignPlan.id,
      title: pendingBudgetSave.title,
      content: pendingBudgetSave.content,
      showTitle: pendingBudgetSave.showTitle
    }

    await updateBudget(params)

    setShowBudgetSaveWarning(false)
    setPendingBudgetSave(null)
    setEditingSectionType(null)
  }

  const cancelBudgetSave = () => {
    setShowBudgetSaveWarning(false)
    setPendingBudgetSave(null)
  }

  const confirmDeleteSection = async () => {
    if (!deletingSectionType) return

    await hideSection({
      masterCampaignPlanId: masterCampaignPlan.id,
      sectionType: deletingSectionType
    })

    setDeletingSectionType(null)
  }

  const handleCancelDelete = () => {
    if (!isHidingSection) {
      setDeletingSectionType(null)
    }
  }

  const handleAddCustomSection = (position: ExecutiveSummaryCustomSectionPosition) => {
    setAddingCustomSectionPosition(position)
  }

  const handleEditCustomSection = (position: ExecutiveSummaryCustomSectionPosition, index: number) => {
    setEditingCustomSection({ position, index })
  }

  const handleDeleteCustomSection = (position: ExecutiveSummaryCustomSectionPosition, index: number) => {
    setDeletingCustomSection({ position, index })
  }

  const handleSaveCustomSection = async (title: string, content: string, showTitle: boolean) => {
    if (addingCustomSectionPosition) {
      const existingSections = executiveSummarySection?.[addingCustomSectionPosition] || []
      await addCustomSection({
        masterCampaignPlanId: masterCampaignPlan.id,
        position: addingCustomSectionPosition,
        existingSections,
        title,
        content,
        showTitle
      })
      setAddingCustomSectionPosition(null)
    } else if (editingCustomSection) {
      const existingSections = executiveSummarySection?.[editingCustomSection.position] || []
      await updateCustomSection({
        masterCampaignPlanId: masterCampaignPlan.id,
        position: editingCustomSection.position,
        existingSections,
        sectionIndex: editingCustomSection.index,
        title,
        content,
        showTitle
      })
      setEditingCustomSection(null)
    }
  }

  const confirmDeleteCustomSection = async () => {
    if (!deletingCustomSection) return

    const existingSections = executiveSummarySection?.[deletingCustomSection.position] || []
    await deleteCustomSection({
      masterCampaignPlanId: masterCampaignPlan.id,
      position: deletingCustomSection.position,
      existingSections,
      sectionIndex: deletingCustomSection.index
    })

    setDeletingCustomSection(null)
  }

  const handleCancelDeleteCustomSection = () => {
    if (!isDeletingCustomSection) {
      setDeletingCustomSection(null)
    }
  }

  const handleEditPageTitle = () => {
    setEditingPageTitle(true)
  }

  const handleSavePageTitle = async (newTitle: string) => {
    await updatePageTitle({
      masterCampaignPlanId: masterCampaignPlan.id,
      pageTitle: newTitle
    })
    setEditingPageTitle(false)
  }

  const isUpdatingAnySection = isUpdatingIntroduction || isUpdatingChannelUtilization || isUpdatingAudienceStrategy || isUpdatingGeographicTargeting || isUpdatingBudget || isAddingCustomSection || isUpdatingCustomSection

  const channelUtilizationHidden = executiveSummarySection?.channelUtilization?.hideAll === true
  const audienceVisible = executiveSummarySection?.audienceStrategy?.hideAll !== true
  const geoVisible = executiveSummarySection?.geographicTargeting?.hideAll !== true
  const budgetHidden = executiveSummarySection?.budget?.hideAll === true

  const getDefaultTitle = (sectionType: SectionType): string => {
    switch (sectionType) {
      case 'introduction':
        return ''
      case 'channelUtilization':
        return 'Channel Utilization'
      case 'audienceStrategy':
        return 'Audience Strategy'
      case 'geographicTargeting':
        return 'Geographic Targeting'
      case 'budget':
        return 'Budget'
    }
  }

  const getDefaultShowTitle = (sectionType: SectionType): boolean => {
    return sectionType !== 'introduction'
  }

  const getDefaultContent = (sectionType: SectionType): string => {
    if (!currentAccount) {
      return ''
    }

    switch (sectionType) {
      case 'introduction':
        return getPlanDescription(currentAccount.name).combined
      case 'channelUtilization':
        return getChannelUtilizationContent(campaigns) ?? ''
      case 'audienceStrategy':
        return masterCampaignPlan.audienceSummary
      case 'geographicTargeting':
        return masterCampaignPlan.geoSummary
      case 'budget':
        return getBudgetContent(campaigns, false)
    }
  }

  const getSectionDisplayName = (sectionType: SectionType): string => {
    switch (sectionType) {
      case 'introduction':
        return 'Introduction'
      case 'channelUtilization':
        return 'Channel Utilization'
      case 'audienceStrategy':
        return 'Audience Strategy'
      case 'geographicTargeting':
        return 'Geographic Targeting'
      case 'budget':
        return 'Budget'
    }
  }

  const getSectionName = (sectionType: SectionType | null): string => {
    if (!sectionType) return 'Section'

    const section = executiveSummarySection?.[sectionType]
    const title = section?.title?.trim()

    if (title && title.length > 0 && section?.showTitle) {
      return title
    }

    return getSectionDisplayName(sectionType)
  }

  const getDeleteModalTitle = (): string => {
    return `Delete ${getSectionName(deletingSectionType)}`
  }

  const getDeleteCustomSectionTitle = (): string => {
    if (!deletingCustomSection) return 'Delete Custom Section'

    const sections = executiveSummarySection?.[deletingCustomSection.position] || []
    const section = sections[deletingCustomSection.index]
    const title = section?.title?.trim()

    if (title && title.length > 0) {
      return `Delete ${title}`
    }

    return 'Delete Custom Section'
  }

  const getDeleteCustomSectionMessage = (): string => {
    if (!deletingCustomSection) return 'Are you sure you want to delete this custom section?'

    const sections = executiveSummarySection?.[deletingCustomSection.position] || []
    const section = sections[deletingCustomSection.index]
    const title = section?.title?.trim()

    if (title && title.length > 0) {
      return `Are you sure you want to delete ${title}?`
    }

    return 'Are you sure you want to delete this custom section?'
  }

  const getEditFlyoutTitle = (): string => {
    return `Edit ${getSectionName(editingSectionType)}`
  }

  return (
    <>
      <ExecutiveSummaryHeader pageTitle={executiveSummarySection?.pageTitle} onEditPageTitle={handleEditPageTitle} showActions={editMode} onClientPdfClick={handlePdfDownload} isGeneratingPdf={isGeneratingPdf} onAllClientPdfsClick={onAllClientPdfsClick} onAllStaffPdfsClick={onAllStaffPdfsClick} staffPdfDisabledReason={staffPdfDisabledReason} />

      {!executiveSummarySection?.introduction?.hideAll && (
        <>
          <PlanDescription campaigns={campaigns} section={executiveSummarySection?.introduction} onEdit={() => handleEditSection('introduction')} onDelete={() => handleDeleteSection('introduction')} showActions={editMode} />
          <EuiSpacer size='xl' />
        </>
      )}

      <RenderCustomSections sections={executiveSummarySection?.aboveCampaignTimeline} position='aboveCampaignTimeline' editMode={editMode} onEdit={handleEditCustomSection} onDelete={handleDeleteCustomSection} />
      {editMode && (
        <>
          <AddCustomSectionButton position='aboveCampaignTimeline' onClick={handleAddCustomSection} />
          <EuiSpacer size='xl' />
        </>
      )}

      <CampaignTimeline campaigns={campaigns} planStartDate={planStartDate} planDurationMonths={planDurationMonths} isLoading={isMasterCampaignStubFetching} isClickable={false} isHoverable={false} />

      <EuiSpacer size='xl' />

      <div ref={ganttChartRef} style={{ position: 'absolute', left: '-99999px', top: '-99999px', width: '1200px', overflow: 'visible' }}>
        <CampaignTimeline campaigns={campaigns} planStartDate={planStartDate} planDurationMonths={planDurationMonths} isLoading={isMasterCampaignStubFetching} ganttChartRef={ganttChartRef} isClickable={false} isHoverable={false} />
      </div>

      <RenderCustomSections sections={executiveSummarySection?.aboveChannelUtilization} position='aboveChannelUtilization' editMode={editMode} onEdit={handleEditCustomSection} onDelete={handleDeleteCustomSection} />
      {!channelUtilizationHidden && (
        <>
          {editMode && (
            <>
              <AddCustomSectionButton position='aboveChannelUtilization' onClick={handleAddCustomSection} />
              <EuiSpacer size='xl' />
            </>
          )}
          <ChannelUtilization campaigns={campaigns} section={executiveSummarySection?.channelUtilization} onEdit={() => handleEditSection('channelUtilization')} onDelete={() => handleDeleteSection('channelUtilization')} showActions={editMode} />
          <EuiSpacer size='xl' />
        </>
      )}

      <RenderCustomSections sections={executiveSummarySection?.aboveAudienceAndGeoSummary} position='aboveAudienceAndGeoSummary' editMode={editMode} onEdit={handleEditCustomSection} onDelete={handleDeleteCustomSection} />
      {(audienceVisible || geoVisible) && editMode && (
        <>
          <AddCustomSectionButton position='aboveAudienceAndGeoSummary' onClick={handleAddCustomSection} />
          <EuiSpacer size='xl' />
        </>
      )}

      <AudienceAndGeoTargeting masterCampaignPlan={masterCampaignPlan} audienceSection={executiveSummarySection?.audienceStrategy} geoSection={executiveSummarySection?.geographicTargeting} onEditAudience={() => handleEditSection('audienceStrategy')} onDeleteAudience={() => handleDeleteSection('audienceStrategy')} onEditGeo={() => handleEditSection('geographicTargeting')} onDeleteGeo={() => handleDeleteSection('geographicTargeting')} showActions={editMode} />

      <EuiSpacer size='xl' />

      <RenderCustomSections sections={executiveSummarySection?.aboveBudget} position='aboveBudget' editMode={editMode} onEdit={handleEditCustomSection} onDelete={handleDeleteCustomSection} />
      {!budgetHidden && (
        <>
          {editMode && (
            <>
              {(executiveSummarySection?.aboveBudget?.length || 0) > 0 && <EuiSpacer size='m' />}
              <AddCustomSectionButton position='aboveBudget' onClick={handleAddCustomSection} />
              <EuiSpacer size='xl' />
            </>
          )}
          {!editMode && (executiveSummarySection?.aboveBudget?.length || 0) > 0 && <EuiSpacer size='m' />}
          <Budget campaigns={campaigns} useMediaPlanWording={false} section={executiveSummarySection?.budget} onEdit={() => handleEditSection('budget')} onDelete={() => handleDeleteSection('budget')} showActions={editMode} />
          <EuiSpacer size='xl' />
        </>
      )}

      <RenderCustomSections sections={executiveSummarySection?.aboveBottom} position='aboveBottom' editMode={editMode} onEdit={handleEditCustomSection} onDelete={handleDeleteCustomSection} />
      {editMode && (
        <>
          <AddCustomSectionButton position='aboveBottom' onClick={handleAddCustomSection} />
        </>
      )}

      {editingSectionType && <EditCustomSectionFlyout section={executiveSummarySection?.[editingSectionType]} defaultTitle={getDefaultTitle(editingSectionType)} defaultContent={getDefaultContent(editingSectionType)} defaultShowTitle={getDefaultShowTitle(editingSectionType)} flyoutTitle={getEditFlyoutTitle()} onSave={handleSaveSection} onClose={() => setEditingSectionType(null)} isLoading={isUpdatingAnySection} showBudgetWarning={editingSectionType === 'budget'} />}

      {(addingCustomSectionPosition || editingCustomSection) && (
        <EditCustomSectionFlyout
          section={editingCustomSection ? executiveSummarySection?.[editingCustomSection.position]?.[editingCustomSection.index] : undefined}
          defaultTitle=''
          defaultContent=''
          defaultShowTitle={true}
          flyoutTitle={addingCustomSectionPosition ? 'Add Custom Section' : 'Edit Custom Section'}
          onSave={handleSaveCustomSection}
          onClose={() => {
            setAddingCustomSectionPosition(null)
            setEditingCustomSection(null)
          }}
          isLoading={isUpdatingAnySection}
        />
      )}

      {deletingSectionType && <AcConfirmModal message={'Are you sure you want to delete this section?'} title={getDeleteModalTitle()} onCancel={handleCancelDelete} onConfirm={confirmDeleteSection} cancelButtonText='Cancel' confirmButtonText='Delete' buttonColor='primary' isLoading={isHidingSection} confirmButtonDisabled={isHidingSection} />}

      {deletingCustomSection && <AcConfirmModal message={getDeleteCustomSectionMessage()} title={getDeleteCustomSectionTitle()} onCancel={handleCancelDeleteCustomSection} onConfirm={confirmDeleteCustomSection} cancelButtonText='Cancel' confirmButtonText='Delete' buttonColor='primary' isLoading={isDeletingCustomSection} confirmButtonDisabled={isDeletingCustomSection} />}

      {showBudgetSaveWarning && (
        <AcConfirmModal
          message={"Once you customize this budget section, it becomes static and won't automatically update with campaign changes. Future edits to campaign budgets may cause the dollar amount and percentages here to become out of sync."}
          title='Save Budget Section'
          onCancel={cancelBudgetSave}
          onConfirm={confirmBudgetSave}
          cancelButtonText='Cancel'
          confirmButtonText='Save'
          buttonColor='primary'
          isLoading={isUpdatingBudget}
          confirmButtonDisabled={isUpdatingBudget}
        />
      )}

      {editingPageTitle && <EditPageTitleFlyout currentTitle={executiveSummarySection?.pageTitle || 'Executive Summary'} defaultTitle='Executive Summary' onSave={handleSavePageTitle} onClose={() => setEditingPageTitle(false)} isLoading={isUpdatingPageTitle} />}
    </>
  )
}

export default ExecutiveSummary
